import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Space, Typography, message } from 'antd';
import { ArrowLeftOutlined, LinkOutlined, SaveOutlined, SwapOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { PageLoader } from '@shared/common/components/ui/busy/busy-indicator';
import {
  compareRuns,
  getSavedComparison,
  saveComparison,
  type ComparisonType,
  type RunComparison,
  type SavedComparison,
} from '@/api/validationRuns';
import { PageHeader } from '@/components/PageHeader';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { FindingDrawer } from '../FindingDrawer';
import { ChooseRunsDrawer } from './ChooseRunsDrawer';
import { ComparisonView } from './ComparisonView';
import { TYPE_LABEL } from './compareWords';
import { useRunLinks } from '@/features/scenario-intelligence/workspace/paths';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// Compare (SI-07, mockup 09). Reached from a run's "Compare with another run" drawer until the Scenario Intelligence
// workspace route (/evaluation/compare/:id) exists:
//   /runs/compare?baseline=22&candidate=26[&type=ModelChange]   two runs, the type they imply or the one chosen
//   /runs/compare/7                                              a saved comparison, re-computed on its stored evidence
// Each load takes a request number and is abandoned when the practice changes, so a late answer for another practice
// never lands on this page (MOD-G1). The server owns every verdict; the page lays it out.
// ─────────────────────────────────────────────────────────────────────────────

export const ComparePage = observer(function ComparePage() {
  const { themeStore, merchantContextStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const navigate = useNavigate();
  const links = useRunLinks();
  const { id: savedIdParam } = useParams();
  const [params, setParams] = useSearchParams();
  const savedId = savedIdParam ? Number(savedIdParam) : null;
  const practice = merchantContextStore.selectedMerchantId;

  const [comparison, setComparison] = useState<RunComparison | null>(null);
  const [saved, setSaved] = useState<SavedComparison | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [chooseOpen, setChooseOpen] = useState(false);
  const [openResultId, setOpenResultId] = useState<number | null>(null);
  const seq = useRef(0);

  const baselineId = saved?.comparison.baselineRunId ?? (params.get('baseline') ? Number(params.get('baseline')) : null);
  const candidateId = saved?.comparison.candidateRunId ?? (params.get('candidate') ? Number(params.get('candidate')) : null);
  const requestedType = (params.get('type') as ComparisonType | null) ?? null;
  // What is being loaded: a saved comparison names its own runs, so only its id matters.
  const loadKey = savedId != null ? `saved:${savedId}` : `runs:${baselineId}:${candidateId}:${requestedType ?? ''}`;

  useEffect(() => {
    const mine = ++seq.current;
    const controller = new AbortController();
    setError(null);
    setComparison(null);
    if (savedId == null && (baselineId == null || candidateId == null)) {
      setSaved(null);
      return () => controller.abort();
    }
    setLoading(true);
    const load = savedId != null
      ? getSavedComparison(savedId, controller.signal).then((s) => { if (mine === seq.current) { setSaved(s); setComparison(s.comparison); } })
      : compareRuns(baselineId!, candidateId!, controller.signal, requestedType).then((c) => { if (mine === seq.current) { setSaved(null); setComparison(c); } });
    load
      .catch((err: Error) => !controller.signal.aborted && mine === seq.current && setError(err.message))
      .finally(() => mine === seq.current && setLoading(false));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadKey carries the ids; a practice switch reloads
  }, [loadKey, practice]);

  const changeType = (type: ComparisonType) => {
    if (baselineId == null || candidateId == null) return;
    if (savedId != null) {
      navigate(links.compare(`?baseline=${baselineId}&candidate=${candidateId}&type=${type}`));
      return;
    }
    const next = new URLSearchParams(params);
    next.set('type', type);
    setParams(next, { replace: true });
  };

  const save = async () => {
    if (!comparison) return;
    setSaving(true);
    try {
      const s = await saveComparison({ baselineRunId: comparison.baselineRunId, candidateRunId: comparison.candidateRunId, type: comparison.type ?? null });
      void message.success(`Saved as comparison #${s.id}. Anyone in this practice with the link sees the same evidence.`);
      navigate(links.saved(s.id));
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'The comparison could not be saved');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    void navigator.clipboard?.writeText(window.location.href).then(
      () => message.success('Link copied'),
      () => message.info(window.location.href),
    );
  };

  const title = savedId != null
    ? `Comparison #${savedId}`
    : baselineId != null && candidateId != null ? `Compare run #${baselineId} with run #${candidateId}` : 'Compare two runs';
  const subtitle = saved
    ? `${saved.title ? `${saved.title} · ` : ''}Saved ${dayjs(saved.savedAtUtc).format('D MMM YYYY HH:mm')}. Anyone in this practice with the link sees the same evidence.`
    : comparison?.type ? `${TYPE_LABEL[comparison.type]} · not saved` : 'Choose a baseline and a candidate of the same agent and practice.';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
      <div>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(baselineId != null ? links.run(candidateId ?? baselineId) : links.runs)}
          style={{ paddingLeft: 0, color: isDark ? colors.slate[300] : colors.slate[500] }}
        >
          {candidateId != null ? `Run #${candidateId}` : 'All runs'}
        </Button>
      </div>
      <PageHeader
        accent="violet"
        icon={<SwapOutlined />}
        title={title}
        subtitle={subtitle}
        actions={
          <Space wrap>
            <Button icon={<LinkOutlined />} onClick={copyLink} disabled={!comparison}>Copy link</Button>
            <Button icon={<SwapOutlined />} onClick={() => setChooseOpen(true)} disabled={baselineId == null}>Choose runs…</Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={() => void save()} loading={saving} disabled={!comparison || saved != null}>
              {saved ? 'Saved' : 'Save comparison'}
            </Button>
          </Space>
        }
      />

      {saved?.changedSinceSaved && <Alert type="info" showIcon title="Changed since it was saved" description={saved.changedSinceSaved} />}

      {savedId == null && (baselineId == null || candidateId == null) && (
        <Alert
          type="info"
          showIcon
          title="Choose two runs to compare"
          description={<span>Open a run and choose <Text strong>Compare with another run</Text>, then <Text strong>Open full comparison</Text>. <Link to={links.runs}>All runs</Link></span>}
        />
      )}

      {loading && !comparison && (
        <PageLoader label="Pairing the two runs" hint="Results are paired by scenario version; each run's model calls are read for who answered and what it cost." />
      )}

      {error && (
        <Alert
          type="error"
          showIcon
          title="Could not compare"
          description={
            <Space direction="vertical" size={6}>
              <span>{`The server said: "${error}"`}</span>
              {baselineId != null && <Button size="small" onClick={() => setChooseOpen(true)}>Choose another run</Button>}
            </Space>
          }
        />
      )}

      {comparison && (
        <ComparisonView comparison={comparison} onTypeChange={changeType} onOpenResult={setOpenResultId} />
      )}

      {baselineId != null && (
        <ChooseRunsDrawer
          open={chooseOpen}
          baselineRunId={baselineId}
          currentCandidateId={candidateId}
          onClose={() => setChooseOpen(false)}
          onChoose={(c) => {
            setChooseOpen(false);
            navigate(links.compare(`?baseline=${baselineId}&candidate=${c}`));
          }}
        />
      )}
      <FindingDrawer resultId={openResultId} onClose={() => setOpenResultId(null)} />
    </div>
  );
});
